import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Avatar, IconButton, Chip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  MenuItem, Select, FormControl, InputLabel, Tooltip, Menu as MuiMenu,
  ImageList, ImageListItem, Checkbox, ListItemText, OutlinedInput,
  Skeleton, Fade, Popover, List, ListItem, ListItemAvatar, Divider,
  Collapse, InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  PushPin as PushPinIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Campaign as CampaignIcon,
  PictureAsPdf as PdfIcon,
  VideoFile as VideoIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  ErrorOutline as UrgentIcon,
  WarningAmber as ImportantIcon,
  Article as ArticleIcon,
  ChatBubbleOutline as CommentIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

// Wall background images
import wallBg1 from '../images/wall-backgrounds/space1.jpg';
import wallBg2 from '../images/wall-backgrounds/space2.jpg';
import wallBg3 from '../images/wall-backgrounds/space3.jpg';
import wallBg4 from '../images/wall-backgrounds/space4.jpg';
import wallBg5 from '../images/wall-backgrounds/space5.jpg';
import wallBg6 from '../images/wall-backgrounds/space6.jpg';
import wallBg7 from '../images/wall-backgrounds/space7.jpg';
import wallBg8 from '../images/wall-backgrounds/space8.jpg';
import wallBg9 from '../images/wall-backgrounds/space9.jpg';
import wallBg10 from '../images/wall-backgrounds/space10.jpg';

const WALL_BACKGROUNDS = [wallBg1, wallBg2, wallBg3, wallBg4, wallBg5, wallBg6, wallBg7, wallBg8, wallBg9, wallBg10];
const randomBg = WALL_BACKGROUNDS[Math.floor(Math.random() * WALL_BACKGROUNDS.length)];

// Helper: format relative time
const timeAgo = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'Hace un momento';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Hace ${days}d`;
  const months = Math.floor(days / 30);
  return `Hace ${months} mes${months > 1 ? 'es' : ''}`;
};

const getFileIcon = (mimetype) => {
  if (!mimetype) return <FileIcon />;
  if (mimetype.startsWith('image/')) return <ImageIcon sx={{ color: '#4caf50' }} />;
  if (mimetype.startsWith('video/')) return <VideoIcon sx={{ color: '#2196f3' }} />;
  if (mimetype === 'application/pdf') return <PdfIcon sx={{ color: '#e53935' }} />;
  return <FileIcon sx={{ color: '#757575' }} />;
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const POST_TYPES = [
  { value: 'general', label: 'General', color: 'default' },
  { value: 'announcement', label: 'Comunicado', color: 'primary' },
  { value: 'notification', label: 'Notificacion', color: 'info' }
];

const PRIORITIES = [
  { value: 'normal', label: 'Normal' },
  { value: 'important', label: 'Importante' },
  { value: 'urgent', label: 'Urgente' }
];

const priorityConfig = {
  normal: { color: 'default', icon: null },
  important: { color: 'warning', icon: <ImportantIcon fontSize="small" /> },
  urgent: { color: 'error', icon: <UrgentIcon fontSize="small" /> }
};

const getAvatarUrl = (avatarPath) => {
  if (!avatarPath) return null;
  return avatarPath.startsWith('http') ? avatarPath : `/api/admin${avatarPath}`;
};

// ============================================
// WallPostCard
// ============================================
const WallPostCard = ({ post, onLike, onPin, onEdit, onDelete, isWriter, wall, currentUserId }) => {
  const { canEdit, canDelete, canAction } = usePermissions();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [likesAnchor, setLikesAnchor] = useState(null);
  const [likesList, setLikesList] = useState([]);
  const [likesLoading, setLikesLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const attachments = post.attachments || [];
  const images = attachments.filter(a => a.mimetype?.startsWith('image/'));
  const videos = attachments.filter(a => a.mimetype?.startsWith('video/'));
  const docs = attachments.filter(a => !a.mimetype?.startsWith('image/') && !a.mimetype?.startsWith('video/'));

  const avatarUrl = getAvatarUrl(post.author_avatar);
  const prioConf = priorityConfig[post.priority] || priorityConfig.normal;
  const typeConf = POST_TYPES.find(t => t.value === post.post_type) || POST_TYPES[0];

  const handleShowLikes = async (e) => {
    if (post.likes_count === 0) return;
    setLikesAnchor(e.currentTarget);
    setLikesLoading(true);
    try {
      const resp = await wall.getLikes(post.id);
      if (resp?.success) setLikesList(resp.data.likes || []);
    } catch { /* ignore */ }
    setLikesLoading(false);
  };

  const handleToggleComments = async () => {
    const willShow = !showComments;
    setShowComments(willShow);
    if (willShow && comments.length === 0) {
      setCommentsLoading(true);
      try {
        const resp = await wall.getComments(post.id);
        if (resp?.success) setComments(resp.data.comments || []);
      } catch { /* ignore */ }
      setCommentsLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || sendingComment) return;
    setSendingComment(true);
    try {
      const resp = await wall.addComment(post.id, newComment.trim());
      if (resp?.success) {
        setComments(prev => [...prev, resp.data]);
        setNewComment('');
        post.comments_count = (post.comments_count || 0) + 1;
      }
    } catch { /* ignore */ }
    setSendingComment(false);
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const resp = await wall.deleteComment(commentId);
      if (resp?.success) {
        setComments(prev => prev.filter(c => c.id !== commentId));
        post.comments_count = Math.max((post.comments_count || 1) - 1, 0);
      }
    } catch { /* ignore */ }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 3,
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.12)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.18)',
        color: 'white',
        transition: 'background 0.2s, border-color 0.2s',
        '&:hover': { background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.3)' },
        ...(post.pinned && { borderColor: 'rgba(102,126,234,0.6)' }),
        ...(post.priority === 'urgent' && { borderLeft: '4px solid', borderLeftColor: 'error.main' }),
        ...(post.priority === 'important' && { borderLeft: '4px solid', borderLeftColor: 'warning.main' })
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Avatar
          src={avatarUrl}
          sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontSize: '1rem', fontWeight: 600 }}
        >
          {post.author_name?.charAt(0)?.toUpperCase()}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{post.author_name}</Typography>
            <Chip label={typeConf.label} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.25)' }} />
            {post.priority !== 'normal' && (
              <Chip icon={prioConf.icon} label={PRIORITIES.find(p => p.value === post.priority)?.label} size="small" color={prioConf.color} sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
            {post.pinned && <PushPinIcon sx={{ fontSize: 16, color: 'primary.main', transform: 'rotate(45deg)' }} />}
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{timeAgo(post.created_at)}</Typography>
        </Box>

        {isWriter && (
          <>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <MuiMenu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
              {canAction('edit_own_wall') && <MenuItem onClick={() => { setMenuAnchor(null); onEdit(post); }}><EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar</MenuItem>}
              {canAction('pin_wall') && <MenuItem onClick={() => { setMenuAnchor(null); onPin(post.id); }}><PushPinIcon fontSize="small" sx={{ mr: 1 }} />{post.pinned ? 'Desfijar' : 'Fijar'}</MenuItem>}
              {canAction('delete_wall') && <MenuItem onClick={() => { setMenuAnchor(null); onDelete(post.id); }} sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Eliminar</MenuItem>}
            </MuiMenu>
          </>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ px: 2.5, pb: 1.5 }}>
        {post.title && (
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: '1.05rem', color: 'white' }}>{post.title}</Typography>
        )}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
          {post.content}
        </Typography>
      </Box>

      {/* Images */}
      {images.length > 0 && (
        <Box sx={{ px: 2.5, pb: 1 }}>
          <ImageList cols={images.length === 1 ? 1 : (images.length === 2 ? 2 : 3)} gap={8} sx={{ m: 0 }}>
            {images.map((img, i) => (
              <ImageListItem key={i} sx={{ cursor: 'pointer', borderRadius: 2, overflow: 'hidden' }} onClick={() => setImagePreview(img.url)}>
                <img src={`/api/admin${img.url}`} alt={img.originalName} loading="lazy"
                  style={{ borderRadius: 8, maxHeight: 300, objectFit: 'cover', width: '100%' }} />
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <Box sx={{ px: 2.5, pb: 1 }}>
          {videos.map((vid, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              <video controls preload="metadata" style={{ width: '100%', maxHeight: 400, borderRadius: 8 }}>
                <source src={`/api/admin${vid.url}`} type={vid.mimetype} />
              </video>
            </Box>
          ))}
        </Box>
      )}

      {/* Documents */}
      {docs.length > 0 && (
        <Box sx={{ px: 2.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {docs.map((doc, i) => (
            <Box key={i} component="a" href={`/api/admin${doc.url}`} target="_blank" rel="noopener noreferrer" download={doc.originalName}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.08)', textDecoration: 'none', color: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' } }}>
              {getFileIcon(doc.mimetype)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: 'rgba(255,255,255,0.85)' }}>{doc.originalName}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>{formatSize(doc.size)}</Typography>
              </Box>
              <DownloadIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.4)' }} />
            </Box>
          ))}
        </Box>
      )}

      {/* Actions bar */}
      <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.1)' }}>
        {canAction('like_wall') && (
        <IconButton size="small" onClick={() => onLike(post.id)} sx={{ mr: 0.5, color: post.liked_by_me ? '#667eea' : 'rgba(255,255,255,0.6)' }}>
          {post.liked_by_me ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
        </IconButton>
        )}
        <Typography
          variant="caption"
          sx={{ mr: 2, color: post.likes_count > 0 ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)', cursor: post.likes_count > 0 ? 'pointer' : 'default', '&:hover': post.likes_count > 0 ? { textDecoration: 'underline' } : {} }}
          onClick={handleShowLikes}
        >
          {post.likes_count > 0 ? `${post.likes_count} me gusta` : ''}
        </Typography>

        <IconButton size="small" onClick={handleToggleComments} sx={{ mr: 0.5, color: showComments ? '#667eea' : 'rgba(255,255,255,0.6)' }}>
          <CommentIcon fontSize="small" />
        </IconButton>
        <Typography variant="caption" sx={{ cursor: 'pointer', color: 'rgba(255,255,255,0.5)' }} onClick={handleToggleComments}>
          {post.comments_count > 0 ? `${post.comments_count} comentario${post.comments_count > 1 ? 's' : ''}` : 'Comentar'}
        </Typography>

        {post.target_campaigns && Array.isArray(post.target_campaigns) && post.target_campaigns.length > 0 && (
          <Chip icon={<CampaignIcon sx={{ fontSize: 14 }} />} label={`${post.target_campaigns.length} camp.`}
            size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', ml: 'auto' }} />
        )}
      </Box>

      {/* Comments section */}
      <Collapse in={showComments} timeout="auto">
        <Box sx={{ px: 2.5, pb: 2, borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.1)' }}>
          {commentsLoading ? (
            <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
          ) : (
            <>
              {comments.map((c) => (
                <Box key={c.id} sx={{ display: 'flex', gap: 1, py: 1 }}>
                  <Avatar src={getAvatarUrl(c.author_avatar)} sx={{ width: 30, height: 30, fontSize: '0.75rem', bgcolor: 'secondary.main' }}>
                    {c.author_name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2, px: 1.5, py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>{c.author_name}</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>{timeAgo(c.created_at)}</Typography>
                      {(c.author_id === currentUserId || isWriter) && (
                        <IconButton size="small" onClick={() => handleDeleteComment(c.id)} sx={{ ml: 'auto', p: 0.25, color: 'rgba(255,255,255,0.4)' }}>
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>
                      {c.content}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {/* New comment input */}
              {canAction('comment_wall') && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Escribe un comentario..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                  multiline
                  maxRows={3}
                  InputProps={{
                    sx: { borderRadius: 3, fontSize: '0.85rem', color: 'rgba(255,255,255,0.9)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' } },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleAddComment} disabled={!newComment.trim() || sendingComment} sx={{ color: newComment.trim() ? '#667eea' : 'rgba(255,255,255,0.3)' }}>
                          {sendingComment ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>

      {/* Likes popover */}
      <Popover
        open={Boolean(likesAnchor)}
        anchorEl={likesAnchor}
        onClose={() => setLikesAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: 2, maxHeight: 300, minWidth: 220 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Les gusta esto</Typography>
        </Box>
        <Divider />
        {likesLoading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        ) : (
          <List dense sx={{ py: 0 }}>
            {likesList.map((l) => (
              <ListItem key={l.agent_id} sx={{ py: 0.5 }}>
                <ListItemAvatar sx={{ minWidth: 36 }}>
                  <Avatar src={getAvatarUrl(l.avatar)} sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                    {l.agent_name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={l.agent_name} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
              </ListItem>
            ))}
          </List>
        )}
      </Popover>

      {/* Image preview dialog */}
      <Dialog open={Boolean(imagePreview)} onClose={() => setImagePreview(null)} maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 2, bgcolor: 'transparent', boxShadow: 'none' } }}>
        {imagePreview && (
          <Box sx={{ position: 'relative' }}>
            <IconButton onClick={() => setImagePreview(null)}
              sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
              <CloseIcon />
            </IconButton>
            <img src={`/api/admin${imagePreview}`} alt="Preview" style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 }} />
          </Box>
        )}
      </Dialog>
    </Paper>
  );
};

// ============================================
// InlineComposer (Twitter/X style)
// ============================================
const InlineComposer = ({ onSave, campaigns, userAvatar, userName }) => {
  const { canAction } = usePermissions();
  const firstName = userName?.split(' ')[0] || '';
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [targetCampaigns, setTargetCampaigns] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleFileAdd = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (files.length + newFiles.length > 5) { alert('Maximo 5 archivos por post'); return; }
    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!content.trim() || saving) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('content', content);
      formData.append('post_type', postType);
      formData.append('priority', priority);
      formData.append('target_campaigns', targetCampaigns.length > 0 ? JSON.stringify(targetCampaigns) : 'null');
      files.forEach(f => formData.append('files', f));
      await onSave(formData);
      setContent(''); setPostType('general'); setPriority('normal');
      setTargetCampaigns([]); setFiles([]); setExpanded(false);
    } catch (err) {
      console.error('Error saving post:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, mb: 2, overflow: 'hidden', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.18)', color: 'white' }}>
      <Box sx={{ display: 'flex', gap: 1.5, p: 2, pb: expanded ? 1 : 2 }}>
        <Avatar src={userAvatar} sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontSize: '1rem', fontWeight: 600 }}>
          {userName?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={expanded ? 2 : 1}
            maxRows={8}
            placeholder={`Que piensas ${firstName}?`}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onFocus={() => setExpanded(true)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '0.95rem', lineHeight: 1.5, color: 'rgba(255,255,255,0.9)' }
            }}
            sx={{ '& .MuiInput-root::before': { display: 'none' }, '& textarea::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 } }}
          />
        </Box>
      </Box>

      {/* Attached files preview */}
      {files.length > 0 && (
        <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {files.map((f, i) => (
            <Chip
              key={i}
              icon={getFileIcon(f.type)}
              label={f.name}
              size="small"
              onDelete={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
              sx={{ maxWidth: 200, color: 'rgba(255,255,255,0.85)', bgcolor: 'rgba(255,255,255,0.1)', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.7)' } } }}
            />
          ))}
        </Box>
      )}

      {/* Toolbar */}
      <Collapse in={expanded || content.length > 0}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Select
            value={postType}
            onChange={(e) => setPostType(e.target.value)}
            size="small"
            variant="standard"
            disableUnderline
            sx={{ fontSize: '0.8rem', minWidth: 90, color: 'rgba(255,255,255,0.8)', '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' }, '& .MuiSelect-select': { py: 0.5 } }}
          >
            {POST_TYPES.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontSize: '0.85rem' }}>{t.label}</MenuItem>)}
          </Select>

          <Select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            size="small"
            variant="standard"
            disableUnderline
            sx={{ fontSize: '0.8rem', minWidth: 80, color: 'rgba(255,255,255,0.8)', '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' }, '& .MuiSelect-select': { py: 0.5 } }}
          >
            {PRIORITIES.map(p => <MenuItem key={p.value} value={p.value} sx={{ fontSize: '0.85rem' }}>{p.label}</MenuItem>)}
          </Select>

          <FormControl size="small" sx={{ minWidth: 120, maxWidth: 200 }}>
            <Select
              multiple
              value={targetCampaigns}
              onChange={(e) => setTargetCampaigns(e.target.value)}
              displayEmpty
              variant="standard"
              disableUnderline
              renderValue={(selected) => {
                if (selected.length === 0) return <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Todas las campanas</Typography>;
                return <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>{selected.map(id => { const c = campaigns.find(c => c.idcampaign === id); return c ? c.descripcion : id; }).join(', ')}</Typography>;
              }}
              sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' }, '& .MuiSelect-select': { py: 0.5 } }}
            >
              {campaigns.map(c => (
                <MenuItem key={c.idcampaign} value={c.idcampaign} sx={{ fontSize: '0.85rem' }}>
                  <Checkbox checked={targetCampaigns.includes(c.idcampaign)} size="small" />
                  <ListItemText primary={c.descripcion} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {canAction('upload_media_wall') && (
          <>
          <Tooltip title="Imagenes">
            <IconButton size="small" component="label" sx={{ color: '#4caf50' }}>
              <ImageIcon fontSize="small" />
              <input type="file" hidden multiple accept="image/*" onChange={handleFileAdd} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Audio / Video">
            <IconButton size="small" component="label" sx={{ color: '#2196f3' }}>
              <VideoIcon fontSize="small" />
              <input type="file" hidden multiple accept="video/*,audio/*" onChange={handleFileAdd} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Documento">
            <IconButton size="small" component="label" sx={{ color: '#ff9800' }}>
              <FileIcon fontSize="small" />
              <input type="file" hidden multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar" onChange={handleFileAdd} />
            </IconButton>
          </Tooltip>
          </>
          )}

          <Box sx={{ flex: 1 }} />

          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={saving || !content.trim()}
            sx={{
              borderRadius: 5, textTransform: 'none', px: 2.5, fontWeight: 600,
              background: 'rgba(102,126,234,0.35)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(102,126,234,0.5)',
              color: 'white',
              boxShadow: 'none',
              '&:hover': { background: 'rgba(102,126,234,0.55)', borderColor: 'rgba(102,126,234,0.7)', boxShadow: '0 0 16px rgba(102,126,234,0.3)' },
              '&.Mui-disabled': { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.25)' }
            }}
          >
            {saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Publicar'}
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
};

// ============================================
// EditPostDialog (only for editing existing posts)
// ============================================
const EditPostDialog = ({ open, onClose, onSave, editPost, campaigns }) => {
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [targetCampaigns, setTargetCampaigns] = useState([]);
  const [files, setFiles] = useState([]);
  const [keepAttachments, setKeepAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editPost) {
      setContent(editPost.content || '');
      setPostType(editPost.post_type || 'general');
      setPriority(editPost.priority || 'normal');
      setTargetCampaigns(editPost.target_campaigns || []);
      setKeepAttachments((editPost.attachments || []).map(a => a.filename));
      setFiles([]);
    }
  }, [editPost, open]);

  const handleFileAdd = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (keepAttachments.length + files.length + newFiles.length > 5) { alert('Maximo 5 archivos por post'); return; }
    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('content', content);
      formData.append('post_type', postType);
      formData.append('priority', priority);
      formData.append('target_campaigns', targetCampaigns.length > 0 ? JSON.stringify(targetCampaigns) : 'null');
      formData.append('keep_attachments', JSON.stringify(keepAttachments));
      files.forEach(f => formData.append('files', f));
      await onSave(formData, editPost?.id);
      onClose();
    } catch (err) { console.error('Error saving post:', err); }
    finally { setSaving(false); }
  };

  if (!editPost) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>Editar publicacion</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField label="Contenido" value={content} onChange={(e) => setContent(e.target.value)} fullWidth multiline minRows={3} maxRows={10} required />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={postType} label="Tipo" onChange={(e) => setPostType(e.target.value)}>
              {POST_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Prioridad</InputLabel>
            <Select value={priority} label="Prioridad" onChange={(e) => setPriority(e.target.value)}>
              {PRIORITIES.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <FormControl size="small" fullWidth>
          <InputLabel>Campanas destino</InputLabel>
          <Select multiple value={targetCampaigns} onChange={(e) => setTargetCampaigns(e.target.value)}
            input={<OutlinedInput label="Campanas destino" />}
            renderValue={(selected) => selected.map(id => { const c = campaigns.find(c => c.idcampaign === id); return c ? c.descripcion : id; }).join(', ')}>
            {campaigns.map(c => (
              <MenuItem key={c.idcampaign} value={c.idcampaign}>
                <Checkbox checked={targetCampaigns.includes(c.idcampaign)} size="small" />
                <ListItemText primary={c.descripcion} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {editPost.attachments?.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Archivos actuales</Typography>
            {editPost.attachments.map(att => (
              <Box key={att.filename} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, p: 0.5, borderRadius: 1,
                bgcolor: keepAttachments.includes(att.filename) ? 'grey.50' : 'error.50',
                opacity: keepAttachments.includes(att.filename) ? 1 : 0.5 }}>
                {getFileIcon(att.mimetype)}
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>{att.originalName}</Typography>
                <IconButton size="small" onClick={() => {
                  if (keepAttachments.includes(att.filename)) setKeepAttachments(prev => prev.filter(f => f !== att.filename));
                  else setKeepAttachments(prev => [...prev, att.filename]);
                }}>
                  {keepAttachments.includes(att.filename) ? <CloseIcon fontSize="small" color="error" /> : <AddIcon fontSize="small" color="success" />}
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {files.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Nuevos archivos</Typography>
            {files.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, p: 0.5, borderRadius: 1, bgcolor: 'grey.50' }}>
                {getFileIcon(f.type)}
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>{f.name}</Typography>
                <Typography variant="caption" color="text.secondary">{formatSize(f.size)}</Typography>
                <IconButton size="small" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}>
                  <CloseIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Button variant="outlined" startIcon={<AttachFileIcon />} component="label" size="small" sx={{ alignSelf: 'flex-start' }}>
          Adjuntar archivos
          <input type="file" hidden multiple onChange={handleFileAdd} />
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !content.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : null}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// WallPage (Main)
// ============================================
const WallPage = () => {
  const { wall, sse } = useApi();
  const { user } = useAuth();
  const { canCreate, canAction } = usePermissions();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [campaigns, setCampaigns] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const currentRole = (user?.role?.role_name || user?.role || 'agent').toLowerCase();
  const currentUserId = user?.agent || user?.id || user?.username || '';
  const canWrite = ['admin', 'superadmin', 'supervisor'].includes(currentRole);
  const userAvatarPath = user?.avatar_path || user?.extra_config?.avatar_path || null;
  const userAvatar = getAvatarUrl(userAvatarPath);
  const userName = user?.name || user?.username || '';

  const loadPosts = useCallback(async (p = 1) => {
    try {
      setLoading(true);
      const resp = await wall.getPosts({ page: p, limit: 20 });
      if (resp?.success) { setPosts(resp.data.posts); setPagination(resp.data.pagination); setPage(p); }
    } catch (err) { console.error('Error loading wall posts:', err); }
    finally { setLoading(false); }
  }, [wall]);

  const loadCampaigns = useCallback(async () => {
    try {
      const resp = await wall.getCampaigns();
      if (resp?.success) setCampaigns(resp.data.campaigns || []);
    } catch { /* ignore */ }
  }, [wall]);

  useEffect(() => { loadPosts(); loadCampaigns(); }, [loadPosts, loadCampaigns]);

  // Listen for SSE new post events to auto-refresh
  useEffect(() => {
    if (!sse?.addEventListener) return;
    const handler = (e) => {
      try { loadPosts(1); } catch { /* ignore */ }
    };
    sse.addEventListener('wall-new-post', handler);
    return () => sse.removeEventListener('wall-new-post');
  }, [sse, loadPosts]);

  const handleSavePost = async (formData, postId) => {
    if (postId) await wall.updatePost(postId, formData);
    else await wall.createPost(formData);
    loadPosts(postId ? page : 1);
  };

  const handleLike = async (postId) => {
    try {
      const resp = await wall.toggleLike(postId);
      if (resp?.success) {
        setPosts(prev => prev.map(p => p.id === postId ? {
          ...p, liked_by_me: resp.data.liked,
          likes_count: resp.data.liked ? p.likes_count + 1 : Math.max(p.likes_count - 1, 0)
        } : p));
      }
    } catch (err) { console.error('Error toggling like:', err); }
  };

  const handlePin = async (postId) => {
    try { await wall.togglePin(postId); loadPosts(page); } catch (err) { console.error('Error toggling pin:', err); }
  };

  const handleDelete = async (postId) => {
    try { await wall.deletePost(postId); setDeleteConfirm(null); loadPosts(page); } catch (err) { console.error('Error deleting post:', err); }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Posts Feed - 50% width */}
      <Box sx={{
        flex: 1, overflow: 'auto', py: 2, display: 'flex', justifyContent: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: `url(${randomBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.35)',
          zIndex: 0,
          transform: 'scale(1.05)'
        }
      }}>
        <Box sx={{ width: '50%', minWidth: 480, maxWidth: 900, position: 'relative', zIndex: 1 }}>
          {/* Inline composer for writers */}
          {canWrite && canAction('publish_wall') && (
            <InlineComposer
              onSave={(formData) => handleSavePost(formData, null)}
              campaigns={campaigns}
              userAvatar={userAvatar}
              userName={userName}
            />
          )}

          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Paper key={i} elevation={0} sx={{ mb: 2, p: 2.5, borderRadius: 3, background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                  <Skeleton variant="circular" width={44} height={44} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                  <Box sx={{ flex: 1 }}><Skeleton width="40%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} /><Skeleton width="20%" height={16} sx={{ bgcolor: 'rgba(255,255,255,0.07)' }} /></Box>
                </Box>
                <Skeleton width="100%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} /><Skeleton width="80%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} /><Skeleton width="60%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
              </Paper>
            ))
          ) : posts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ArticleIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.25)', mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.6)' }}>No hay publicaciones aun</Typography>
              {canWrite && <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.35)', mt: 1 }}>Crea la primera publicacion para tu equipo</Typography>}
            </Box>
          ) : (
            <>
              {posts.map(post => (
                <Fade key={post.id} in timeout={300}>
                  <div>
                    <WallPostCard
                      post={post} onLike={handleLike} onPin={handlePin}
                      onEdit={(p) => { setEditPost(p); setEditDialogOpen(true); }}
                      onDelete={(id) => setDeleteConfirm(id)}
                      isWriter={canWrite} wall={wall} currentUserId={currentUserId}
                    />
                  </div>
                </Fade>
              ))}
              {pagination.pages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, py: 2 }}>
                  {Array.from({ length: pagination.pages }, (_, i) => (
                    <Button key={i} size="small" variant={page === i + 1 ? 'contained' : 'outlined'}
                      onClick={() => loadPosts(i + 1)} sx={{ minWidth: 36, borderRadius: 2, ...(page !== i + 1 && { color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.25)' }) }}>{i + 1}</Button>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <EditPostDialog open={editDialogOpen} onClose={() => { setEditDialogOpen(false); setEditPost(null); }}
        onSave={handleSavePost} editPost={editPost} campaigns={campaigns} />

      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Eliminar publicacion</DialogTitle>
        <DialogContent><Typography>Estas seguro de que deseas eliminar esta publicacion? Esta accion no se puede deshacer.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm)}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WallPage;
